import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, FormControl, Button, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import { withRouter } from "react-router-dom"
import Loader from '../../components/UI/Loader/Loader';
import Table, { tableOrigin } from '../../components/UI/Table/Table';
import { ReactComponent as InfoCircleLightIcon } from '../../assets/infoCircleLight.svg';
import { Formik } from "formik";
import * as Yup from "yup";
import graphQLClientInstance from '../../axios-graphql-client';
import axios from 'axios';
import Bottleneck from 'bottleneck';
import classes from './Registers.module.css';
import {
  scrollToRef,
  backgroundImages,
  calcPercentage,
  calcRemaining,
  downloadBlob,
  registerTypeOptions,
  registersReturnTypes,
  getRegistersQuery,
  getCountriesFromSession,
  getCountryNamesFromSession,
  renderTooltipOverlay,
  downloadExcelFileNames,
  downloadExcelWorksheetTitles,
  API_URLS
} from '../../shared/utility';
import { generatexlx } from '../../exportXls';
import SearchFormContainer from '../../components/UI/SearchFormContainer/SearchFormContainer';
import ResultsHeader from '../../components/UI/ResultsHeader/ResultsHeader';
import { Translation, useTranslation } from "react-i18next";
import CustomBackground from "../../components/UI/CustomBackground/CustomBackground";
import { ICountryOption } from "../SearchVarieties/SearchVarieties";

import { useMatomo } from '@datapunt/matomo-tracker-react';

const Registers = (props: any) => {
  const { trackPageView } = useMatomo();

  React.useEffect(() => {
    trackPageView({documentTitle: "Registers"});
    // eslint-disable-next-line
  }, []);

  const { t, i18n } = useTranslation();
  const [formValues, setFormValues] = useState([]);
  const [data, setData] = useState([]);
  const [completedPromises, setCompletedPromises] = useState<number>(0);
  const [generateExcel, setGenerateExcel] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const totalCountRef = React.useRef(0);
  totalCountRef.current = totalCount;
  const [excelModalShow, setExcelModalShow] = useState(false);
  const resultsRef = React.useRef(null);

  const handleExcelModalClose = () => setExcelModalShow(false);
  const handleExcelModalShow = () => setExcelModalShow(true);

  const [init, setInit] = useState(false);
  const initRef = React.useRef(false);
  initRef.current = init;

  const [pageSize, setPageSize] = useState(0);
  const pageSizeRef = React.useRef(20)
  pageSizeRef.current = pageSize;

  const [sortBy, setSortBy] = useState([]);
  const sortByRef = React.useRef([])
  sortByRef.current = sortBy;

  const fetchData = React.useCallback(({ pageSize: tablePageSize, pageIndex: tablePageIndex, sortBy: tableSortBy, values }) => {
    setData([]);
    setPageSize(tablePageSize);
    setPageIndex(tablePageIndex);
    setSortBy(tableSortBy);
    setIsLoading(true);

    if (tablePageIndex === 0) {
      setPageCount(0);
      setTotalCount(0);
    }

    graphQLClientInstance.post(API_URLS.GRAPHQL, getRegistersQuery(values, tablePageSize, tablePageIndex, tableSortBy))
      .then(res => {
        if (res.data.data) {
          setTotalCount(res.data.data.registersSearch.total);
          setPageCount(Math.ceil(res.data.data.registersSearch.total/tablePageSize));
          setData(res.data.data.registersSearch.data);
        }
        setIsLoading(false);
        if (initRef.current) {
          scrollToRef(resultsRef);
        }
        setInit(true);
      })
      .catch(err => {
        setIsLoading(false);
        console.log(err);
      })

  }, [])

  const limiter = new Bottleneck({
    minTime: 200,
    maxConcurrent: 3,
  });

  const completedPromisesRef = React.useRef(completedPromises);

  const generatePromises = async (values:any, totalItems: number, sorting: any) => {
    completedPromisesRef.current = 0;
    let i = 0;
    let pageIdx = 0;
    const promiseArray: Array<Promise<any>> = [];
    while (i < totalItems) {
      const body = getRegistersQuery(values, 1000, pageIdx, sorting);
      const promise = limiter.schedule(() => graphQLClientInstance.post(API_URLS.GRAPHQL, body))
      promiseArray.push(promise);
      i += 1000;
      pageIdx++;
    }

    let dataArr: Array<any> = [];
    await axios.all(promiseArray)
      .then(axios.spread((...res) => {
        dataArr = res.map(r => r.data.data.registersSearch.data);
      }))
    return dataArr;
  };

  const completedPromisesCounter = (count: number) => {
    completedPromisesRef.current = count;
    setCompletedPromises(count);
  };

  // state to keep track of resolved promises
  limiter.on("done", function (info) {
    completedPromisesCounter(completedPromisesRef.current + 1);
  });

  const downloadExcelFile = async ({ values }: { values: any }) => {
    setGenerateExcel(false);
    let myData: any[] = [];
    await generatePromises(values, totalCountRef.current, sortByRef.current)
      .then((res) => {
        if (res) {
          myData = res;
        }
      })
      .catch((err) => {
        console.log(err);
      });

    const flattenData: any[] = myData.flat(2).map((dataRow: any) => {
      return {
        ...dataRow,
        countryid: `${dataRow?.countryid} - ${getCountryNamesFromSession(dataRow.countryid)}`,
        publicationtypeid: `${dataRow?.publicationtypeid} - (${dataRow.publicationtypeid?t(`register_types.${dataRow.publicationtypeid.toLowerCase()}`):""})`,
        all_contributions: dataRow?.all_contributions?.[0]?.pub_date
      }
    });
    let file = "";
    setGenerateExcel(true);
    file = await generatexlx(
      flattenData,
      [
        registersReturnTypes.COUNTRY,
        registersReturnTypes.REGISTER_TYPE,
        registersReturnTypes.REGISTER_NAME,
        registersReturnTypes.ORGANIZATION,
        registersReturnTypes.LAST_CONTRIBUTION
      ],
      downloadExcelWorksheetTitles.REGISTERS_WORKSHEET
    ).catch((err) => {
      console.log(err);
    });

    if (file) {
      const blob = new Blob([file], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
      downloadBlob(blob, downloadExcelFileNames.REGISTERS);
    }
    setExcelModalShow(false);
  };

  useEffect(() => {
    if (completedPromises) {
      handleExcelModalShow();
    }
  }, [completedPromises])

  const downloadExcel = React.useCallback(
    ({ values }) => {
      downloadExcelFile({ values: values });
    }, []
  );

  const downloadExcelModal = (
    <Modal
      centered
      keyboard={false}
      backdrop="static"
      show={excelModalShow}
      onHide={handleExcelModalClose}
      >
      <Modal.Header>
        <Modal.Title>
          {t('downloading_results')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProgressBar
          className="w-100"
          now={calcPercentage(completedPromises, (totalCount/1000))}
          label={`${calcPercentage(completedPromises, (totalCount/1000))}%`}
        />
        <br></br>
        <div>
          {t("results_downloaded_counter", {current: calcRemaining((completedPromises*1000) , totalCount), total: totalCount})}
        </div>
        <div>
          {generateExcel?
            <>
              {t("generate_excel")} <Spinner animation="border" size="sm" variant="primary" />
            </>
          :""}
        </div>
      </Modal.Body>
    </Modal>
  )

  const initialValues: {[key: string]: any} = {
    countryid: "",
    registerType: ""
  };

  const form = (
    <Formik
      initialValues = {initialValues}
      validationSchema = {Yup.object().shape({})}
      onSubmit = {(values: any, { setFieldValue, setSubmitting }) => {
        const payload = {
          ...values
        };
        setPageIndex(0);
        setFormValues(payload);
        setSubmitting(false);
        setIsLoading(true);
      }
    }
    >
      {formProps => (
        <Form
          className={classes.searchForm}
          onSubmit={(e) => {
            formProps.handleSubmit();
            e.preventDefault();
          }}>
          <Row className={classes.searchFieldsRow}>
            <Col md="6" id="countriesContainer">
              <Form.Group controlId="searchForm.countries">
                <Form.Label className="w-100">
                  {t("country")}
                  {renderTooltipOverlay(
                    t("tooltips_registers.country"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                <Form.Control
                  as="select"
                  name="countryid"
                  value={formProps.values.countryid}
                  onChange={formProps.handleChange}
                >
                  <option value={""}>{t('all')}</option>
                  {
                    getCountriesFromSession()?.
                    filter((country: ICountryOption) => !["EU", "NON-EU"].includes(JSON.parse(country.value)?.countryid))
                    .map((country: ICountryOption, index: number) => {
                      return (
                        <option key={`country-option-${index}`} value={JSON.parse(country.value)?.countryid}>
                          {country.label}
                        </option>
                      )
                    })
                  }
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md="6" id="registerTypeContainer" key="registerTypeContainer">
              <Form.Group controlId="searchForm.registerType">
                <Form.Label className="w-100">
                  {t("register_type")}
                  {renderTooltipOverlay(
                    t("tooltips_registers.register_type"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                 <FormControl
                  as="select"
                  name="registerType"
                  value={formProps.values.registerType}
                  onChange={formProps.handleChange}
                >
                  <option value={""}>{t('all')}</option>
                  {
                    registerTypeOptions(true).map((registerType: any, index: number) => {
                      return (
                        <option key={`register-type-option-${index}`} value={registerType.value}>
                          {registerType.label}
                        </option>
                      )
                    })
                  }
                </FormControl>
              </Form.Group>
            </Col>
          </Row>
          <Row className={classes.actionsRow}>
            <Col md="12">
              <Button id="submitSearchForm" variant="primary" type="submit" disabled={formProps.isSubmitting} className="float-right">{t("search")}</Button>
              <Button id="clearFields" variant="link" onClick={() => formProps.resetForm()} className="float-right mr-2">{t("clear_fields")}</Button>
              <Button id="help" variant="link" onClick={() => window.open(`help/${i18n.language}/Registers.html`, '_blank')} className="float-right mr-2">{t("help")}</Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );

  const selectedColumns = [
    {
      name: registersReturnTypes.COUNTRY,
      label: <Translation>{(translate) =>  translate('country')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 0
    },
    {
      name: registersReturnTypes.REGISTER_TYPE,
      label: <Translation>{(translate) =>  translate('register_type')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 2
    },
    {
      name: registersReturnTypes.REGISTER_NAME,
      label: <Translation>{(translate) =>  translate('register_name')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 3
    },
    {
      name: registersReturnTypes.ORGANIZATION,
      label: <Translation>{(translate) =>  translate('organization')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 4,
    },
    {
      name: registersReturnTypes.LAST_CONTRIBUTION_DATA,
      label: <Translation>{(translate) =>  translate('last_contribution')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 5,
    }
  ];

   const tableColumns = React.useMemo(() => {
    return [
      ...selectedColumns.map((item: any) => { return {...item, Header: item.label, accessor: item.name}})
    ]
  }, [selectedColumns]);

  const searchContainer =
  <CustomBackground image={backgroundImages.SPECIES}>
    <SearchFormContainer
      title={t("search_registers.title")}
      subtitle={t("search_registers.subtitle")}
      >
      {form}
    </SearchFormContainer>
  </CustomBackground>

  return (
    <>
    <Loader show={isLoading}></Loader>
    {props.modal?
        form:
        searchContainer
    }
    <Container fluid style={{display: !(isLoading)?'block':'none'}}>
        <ResultsHeader
          totalCount={totalCount}
          resultsRef={resultsRef}
        />
        <Table
          columns={tableColumns}
          origin={tableOrigin.REGISTERS}
          hideColumnsButton={true}
          isSortable={true}
          data={data}
          modal={props.modal}
          fetchData={fetchData}
          downloadExcel={downloadExcel}
          pageCount={pageCount}
          totalCount={totalCount}
          pageIndex={pageIndex}
          formValues={formValues}
          showPagination={true}
          showPaginationTop={false}
          showPaginationBottom={true}
          pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        />
        {downloadExcelModal}
    </Container>
    </>
  );

}

export default withRouter(Registers);
